import { assert, runInDebug } from '@ember/debug';

import {
  ACTION,
  ARRAY,
  BOOLEAN,
  CONFIG_OPTION,
  NUMBER,
  OBJECT,
  STRING,
  VALIDATABLE_TYPES,
} from './constants';
import { configOptionAssertions, defaultAssertions } from './assertions';

/**
 * Base class for creating validation objects.
 * This class is used to define the type of validation, the assertions to apply,
 * and the list of validations to perform.
 * @argument {string} type the expected data type to validate against
 * @argument {object} assertions an object with attributes reflecting the available assertions to use for validation
 * @argument {object} validations an object with attributes reflecting the list of arguments to validate
 * This class is primarily used as a base for more specific validation classes
 * (e.g., `StringValidation`, `NumberValidation`).
 */
class ValidationObject {
  validations = {};

  constructor(type, assertions, validations) {
    runInDebug(() => {
      assert(
        `provided type ${type} for ${this.constructor.name} is invalid`,
        VALIDATABLE_TYPES.includes(type)
      );

      this.assertions = assertions ?? defaultAssertions;
      this.type = type;

      Object.keys(this.assertions).forEach(key => (this.validations[key] = validations[key]));
    });
  }
}

/**
 * Class for validating Action values
 * extends ValidationObject class
 */
export class ActionValidation extends ValidationObject {
  constructor() {
    super(ACTION, undefined, ...arguments);
  }
}

/**
 * Class for validating Array values
 * extends ValidationObject class
 */
export class ArrayValidation extends ValidationObject {
  constructor() {
    super(ARRAY, undefined, ...arguments);
  }
}

/**
 * Class for validating Boolean values
 * extends ValidationObject class
 */
export class BooleanValidation extends ValidationObject {
  constructor() {
    super(BOOLEAN, undefined, ...arguments);
  }
}

/**
 * Class for validating ConfigOption values
 * extends ValidationObject class
 */
export class ConfigOptionValidation extends ValidationObject {
  constructor() {
    super(CONFIG_OPTION, configOptionAssertions, ...arguments);
  }
}

/**
 * Class for validating Number values
 * extends ValidationObject class
 */
export class NumberValidation extends ValidationObject {
  constructor() {
    super(NUMBER, undefined, ...arguments);
  }
}

/**
 * Class for validating Object values
 * extends ValidationObject class
 */
export class ObjectValidation extends ValidationObject {
  constructor() {
    super(OBJECT, undefined, ...arguments);
  }
}

/**
 * Class for validating String values
 * extends ValidationObject class
 */
export class StringValidation extends ValidationObject {
  constructor() {
    super(STRING, undefined, ...arguments);
  }
}

// Mapping of type constants to validation classes
export const classesByType = {
  [ACTION]: ActionValidation,
  [ARRAY]: ArrayValidation,
  [BOOLEAN]: BooleanValidation,
  [CONFIG_OPTION]: ConfigOptionValidation,
  [NUMBER]: NumberValidation,
  [OBJECT]: ObjectValidation,
  [STRING]: StringValidation,
};

export default {
  ActionValidation,
  ArrayValidation,
  BooleanValidation,
  ConfigOptionValidation,
  NumberValidation,
  ObjectValidation,
  StringValidation,
  classesByType,
};
